import { create } from 'zustand'

export type CallState = 'idle' | 'connecting' | 'ringing' | 'in_call' | 'ended'

interface CallStore {
  state: CallState
  leadName: string | null
  leadPhone: string | null
  companyId: string | null
  refId: string | null
  isMuted: boolean
  durationSeconds: number
  isMinimized: boolean

  // internal
  _timerId: ReturnType<typeof setInterval> | null

  // actions (called by sip.ts)
  _setCallState: (s: CallState) => void
  _setLead: (name: string, phone: string, companyId: string, refId: string) => void

  // UI actions
  toggleMute: () => void
  toggleMinimize: () => void
  reset: () => void
}

export const useCallStore = create<CallStore>((set, get) => ({
  state: 'idle',
  leadName: null,
  leadPhone: null,
  companyId: null,
  refId: null,
  isMuted: false,
  durationSeconds: 0,
  isMinimized: false,
  _timerId: null,

  _setLead: (name, phone, companyId, refId) =>
    set({ leadName: name, leadPhone: phone, companyId, refId }),

  _setCallState: (s) => {
    const prev = get()

    // Start timer when call is answered
    if (s === 'in_call' && prev.state !== 'in_call') {
      const id = setInterval(() => {
        set(st => ({ durationSeconds: st.durationSeconds + 1 }))
      }, 1000)
      set({ state: s, _timerId: id })
      return
    }

    // Stop timer on end/idle
    if ((s === 'ended' || s === 'idle') && prev._timerId) {
      clearInterval(prev._timerId)
      set({ state: s, _timerId: null })
      return
    }

    set({ state: s })
  },

  toggleMute: () => set(st => ({ isMuted: !st.isMuted })),

  toggleMinimize: () => set(st => ({ isMinimized: !st.isMinimized })),

  reset: () => {
    const { _timerId } = get()
    if (_timerId) clearInterval(_timerId)
    set({
      state: 'idle',
      leadName: null,
      leadPhone: null,
      companyId: null,
      refId: null,
      isMuted: false,
      durationSeconds: 0,
      isMinimized: false,
      _timerId: null,
    })
  },
}))
